import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Cloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const gb = (b) => `${((Number(b) || 0) / 1e9).toFixed(1)} GB`;

export const OneDriveCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [params] = useSearchParams();
  const load = useCallback(() => api.get("/onedrive/status").then(({ data }) => setS(data)).catch(() => setS(false)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const g = params.get("onedrive");
    if (g === "connected") toast.success("OneDrive connected");
    if (g === "failed") toast.error("OneDrive wasn't connected. See the reason on the OneDrive card.");
  }, [params]);
  const act = async (key, url, body) => {
    setBusy(key);
    try {
      const { data } = await api.post(url, body);
      if (data.url) { window.location.href = data.url; return; }
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  if (!s) return null;
  const label = !s.connected ? "Not connected" : s.state !== "ok" ? "Reconnect needed" : `Connected as ${s.email}`;
  return (
    <section className="settings-card" data-testid="onedrive-card">
      <h2 className="text-base font-semibold text-neutral-900 inline-flex items-center gap-2"><Cloud size={18} /> OneDrive</h2>
      <p className="text-sm mt-2" data-testid="onedrive-status">
        <span className={`storage-dot ${label.startsWith("Connected") ? "ok" : "bad"}`} /> {label}
        {s.quota && <small className="block text-neutral-500" data-testid="onedrive-quota">{gb(s.quota.used)} used · {gb(s.quota.remaining)} free of {gb(s.quota.total)}</small>}
      </p>
      {!s.connected && s.last_error && (
        <p className="text-xs text-red-700 mt-1" data-testid="onedrive-last-error">
          Last connect attempt failed: {s.last_error.message}{s.last_error.aadsts ? ` (${s.last_error.aadsts})` : ""}
        </p>
      )}
      {!s.configured && <p className="text-xs text-neutral-500 mt-1" data-testid="onedrive-not-configured">Waiting for the Microsoft app client ID and secret.</p>}
      {s.configured && !s.live && <p className="text-xs text-neutral-500 mt-1" data-testid="onedrive-live-only">Connect on the live site (webberdynasty.com).</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        <button className="btn-outline inline-flex items-center gap-2" disabled={!s.configured || !s.live || !!busy} onClick={() => act("connect", "/onedrive/connect")} data-testid="onedrive-connect">
          {busy === "connect" && <Loader2 size={14} className="animate-spin" />}{s.connected ? "Reconnect" : "Connect"}
        </button>
        {s.connected && (
          <button className="btn-outline" disabled={!!busy} data-testid="onedrive-disconnect"
            onClick={() => window.confirm("Disconnect OneDrive?") && act("disconnect", "/onedrive/disconnect")}>Disconnect</button>
        )}
      </div>
    </section>
  );
};
